import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

const benefitKeys = [
  { icon: 'ri-search-eye-line', titleKey: 'whyus.b1.title', descKey: 'whyus.b1.desc' },
  { icon: 'ri-shield-check-line', titleKey: 'whyus.b2.title', descKey: 'whyus.b2.desc' },
  { icon: 'ri-file-list-3-line', titleKey: 'whyus.b3.title', descKey: 'whyus.b3.desc' },
  { icon: 'ri-route-line', titleKey: 'whyus.b4.title', descKey: 'whyus.b4.desc' },
  { icon: 'ri-car-washing-line', titleKey: 'whyus.b5.title', descKey: 'whyus.b5.desc' },
  { icon: 'ri-vip-crown-line', titleKey: 'whyus.b6.title', descKey: 'whyus.b6.desc' },
];

export default function WhyUs() {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.scroll-reveal').forEach((el, i) => {
              setTimeout(() => el.classList.add('visible'), i * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-16 sm:py-28 bg-[#050505] relative overflow-hidden">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-purple-900/10 blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        {/* Header */}
        <div className="text-center mb-16 scroll-reveal">
          <div className="inline-flex items-center gap-2 border border-purple-500/30 px-4 py-2 rounded-full mb-6">
            <span className="text-purple-400 text-xs tracking-widest uppercase font-medium">{t('whyus.label')}</span>
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white mb-4" style={{ letterSpacing: '0.03em' }}>
            {t('whyus.title1')}{' '}
            <span className="gradient-text-purple">{t('whyus.title2')}</span>
          </h2>
          <p className="font-body text-white/50 text-base max-w-2xl mx-auto leading-relaxed">
            {t('whyus.subtitle')}
          </p>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {benefitKeys.map((benefit, i) => (
            <div
              key={benefit.titleKey}
              className="scroll-reveal glass-card glass-card-hover rounded-2xl p-8 transition-all duration-400 group cursor-default"
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-purple-900/30 border border-purple-500/20 mb-6 group-hover:bg-purple-600/30 group-hover:border-purple-400/40 transition-all duration-300">
                <i className={`${benefit.icon} text-purple-400 text-2xl group-hover:text-purple-300 transition-colors duration-300`} />
              </div>
              <h3 className="font-heading text-xl text-white mb-3" style={{ letterSpacing: '0.04em' }}>
                {t(benefit.titleKey)}
              </h3>
              <p className="text-white/50 text-sm leading-relaxed">
                {t(benefit.descKey)}
              </p>
              <div className="mt-6 flex items-center gap-2 text-purple-400/60 group-hover:text-purple-400 transition-colors duration-300">
                <span className="text-xs tracking-widest uppercase font-medium">{t('whyus.more')}</span>
                <i className="ri-arrow-right-line text-sm" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}